import { Button, FormattedMessageDiv, FormInput, FormWrapper } from "@odmonorepo/core-components";
import { useState } from "react";
import { Card, Stack } from "react-bootstrap";
import { BsCheckCircle } from "react-icons/bs";
import ErrorAlert from "./ErrorAlert";
import * as yup from 'yup';
import { useMutation } from "@tanstack/react-query";
import { unifiedApiClient } from "merchant-api/unified";
import { getCaptchaHeaders, isCaptchaFailure } from "merchant-api/vendor/google";

interface Props {
  OTPKey?: string;
  emailMasked?: string;
  onVerify?: () => void;
}

interface OTPRequestError {
  status?: number;
  data?: unknown;
}

interface OTPNotifyRequest {
  email?: string;
  key?: string;
}

interface OTPVerifyRequest extends OTPNotifyRequest {
  otp: string;
}

interface OTPNotifyResponse {
  emailMasked: string;
  key: string;
  error?: string;
}

type OTPVerifyResponse = Record<string, never>;

const notifyOTP = async (data: OTPNotifyRequest) => {
  const result = await unifiedApiClient.POST('/public/otp/notify', { body: data } as never);

  if (!result.response.ok) throw { status: result.response.status, data: result.error } satisfies OTPRequestError;

  return result.data as unknown as OTPNotifyResponse;
};

const verifyOTP = async (data: OTPVerifyRequest) => {
  const result = await unifiedApiClient.POST('/public/otp/verify', { body: data, headers: await getCaptchaHeaders() } as never);

  if (!result.response.ok) throw { status: result.response.status, data: result.error } satisfies OTPRequestError;

  return result.data as unknown as OTPVerifyResponse;
};

export const VerifyCode = ({OTPKey, emailMasked, onVerify = () => {}}: Props) => {
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<number>();
  const { mutate: submitOTP, isPending: verifying } = useMutation<OTPVerifyResponse, OTPRequestError, OTPVerifyRequest>({
    mutationFn: verifyOTP,
    // The Login Server checks the captcha before the OTP, so the code is not consumed and one
    // retry (which sends a fresh token) is safe.
    retry: (failureCount, error) => failureCount < 1 && isCaptchaFailure(error),
    throwOnError: false,
    onError: error => setError(error.status),
    onSuccess: onVerify,
  });

  const { mutate: sendOTP, isPending: notifying } = useMutation<OTPNotifyResponse, OTPRequestError, OTPNotifyRequest>({
    mutationFn: notifyOTP,
    throwOnError: false,
    onError: error => setError(error.status),
    onSuccess: () => {
      setSent(true);
      setError(undefined);
      setTimeout(() => setSent(false), 5000);
    }
  });

  const schema = yup.object({
    otp: yup
      .string()
      .required('Enter your security code.')
      .matches(/^[a-zA-Z0-9]{6}$/, 'Invalid security code. Check that your security code was entered correctly.'),
  });

  return (
  <Card style={{ maxWidth: '600px' }} className='p-5'>
    <ErrorAlert status={error} />
    <FormWrapper schema={schema} reValidateMode="onSubmit" onSubmit={data => (setError(undefined), submitOTP({ otp: data.otp, key: OTPKey }))}>
      <Stack gap={5}>
        <div>
          <FormattedMessageDiv className="h4" id="OTP.title" />
          <FormattedMessageDiv id="OTP.text" values={{ email: emailMasked }} />
        </div>
        <FormInput
          name="otp"
          useYupErrors
          onInput={e => {
            const input = e.target as HTMLInputElement;
            const value = input.value.toUpperCase().slice(0, 6);
            input.value = value;
          }}
        />
        <div>
          <Button
            className="w-100"
            variant="primary"
            loading={verifying}
            loadingText="Submitting..."
            type='submit'
          >
            Submit Code
          </Button>
          <div className="d-flex justify-content-center align-items-center mt-3" style={{ height: '40px' }}>
            {!sent
              ? (
                <Button
                  variant="link"
                  loading={notifying}
                  loadingText="Sending..."
                  onClick={() => (setError(undefined), sendOTP({ key: OTPKey }))}
                  className="p-0 border-0 m-1 fs-"
                >
                  Send new code
                </Button>
              ) : (
                <Stack direction="horizontal" gap={2} className="align-items-center text-success">
                  <BsCheckCircle size="24px"/>
                  <div className="me-3">Sent</div>
                </Stack>
              )
            }
          </div>
        </div>
          </Stack>
    </FormWrapper>
  </Card>
  );
};
